import { Core } from "@faire/design-tokens";
import { SHOWROOM_PATH_IDENTIFIER } from "@faire/retailer-shared/categories/pathIdentifier";
import trackClick from "@faire/web-api/api/click-track/post";
import { trackApparelDiscoveryShowroomV2Click } from "@faire/web-api/events/apparelDiscovery/click/showroomV2";
import { trackApparelDiscoveryShowroomV2View } from "@faire/web-api/events/apparelDiscovery/view/showroomV2";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { path as categoryShowroom } from "@faire/web-api/routes/category/Showroom";
import productRoute from "@faire/web-api/routes/product/productToken";
import { optimizeProductImageUrlFillVertical } from "@faire/web/common/images/optimizeImages";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { formatMoney, RoundingStrategy } from "@faire/web/common/money";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { ButtonProps } from "@faire/web/slate/Button/types";
import { Column, Flex, IFlexProps } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { BaseFiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { FiniteScrollCarousel } from "@faire/retailer/components/Carousel/FiniteScrollCarousel/FiniteScrollCarousel";
import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { ShowroomTileBadge } from "@faire/retailer/components/Showroom/ShowroomTileBadge";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE } from "@faire/retailer/features/Marketplace/styleConsts";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer/lib/verticalAspectRatio";
import { Category } from "@faire/retailer/models/Category/Category";

interface IProps {
  layoutElement: ILayoutElement;
  className?: string;
  requestId?: string;
  category?: Category;
}

const ITEMS_PER_SLIDES = 2;
const ITEMS_GAP = 4;
const IMAGE_WIDTH = 300;

const SingleProductTile: React.FC<{
  onClick: (
    eventSource: string,
    token?: string,
    clickType?: IClickInfo.Type
  ) => () => void;
  leanProductTile?: ILeanProductTile;
  category?: Category;
  filterKeys?: string[];
}> = ({ leanProductTile, onClick, category, filterKeys }) => {
  const product = leanProductTile?.product;
  const isSSR = useIsSSR();

  if (!leanProductTile || !product?.token) {
    return null;
  }

  const image = leanProductTile.best_image;

  return (
    <StyledLink
      to={productRoute(product.token, {
        refC: category?.navigationPath,
        refF: filterKeys?.length ? filterKeys.join(",") : undefined,
      })}
      onClick={onClick(product.token, product.token, IClickInfo.Type.PRODUCT)}
    >
      <StyledShowroomBadge
        customEventName="showroom-product-carousel-banner"
        token={product.token}
      />
      <AspectRatioPicture
        $width="100%"
        $ratio={VERTICAL_ASPECT_RATIO}
        $hideBorder
      >
        <img
          src={
            image
              ? optimizeProductImageUrlFillVertical(image, isSSR, IMAGE_WIDTH)
              : EAEAEA_PIXEL
          }
        />
      </AspectRatioPicture>
      <Typography
        color={Core.text.inverse}
        variant="paragraphSansRegular"
        maxLines={2}
      >
        {product.name}
      </Typography>
      <WholesalePrice>
        <StrictLocalMsg
          defaultMessage="{wholesalePrice} WSP"
          values={DEPRECATED_backwardsCompatibleValues({
            wholesalePrice: formatMoney({
              money:
                leanProductTile.min_option_wholesale_price ??
                IFaireMoney.build({ amount_cents: 0 }),
              roundingStrategy: RoundingStrategy.DROP_CENTS,
            }),
          })}
        />
      </WholesalePrice>
    </StyledLink>
  );
};

const ShowroomEducationCarouselBase: React.FC<IProps> = ({
  layoutElement,
  className,
  requestId,
  category,
}) => {
  const peekGapPercentage = useResponsiveValue({
    mobileAndAbove: 8,
    tabletAndAbove: 18,
    xLargeAndAbove: 27,
  });
  const eventName = `${layoutElement.type}-${category?.mostSpecificPathIdentifier}`;
  const onClick =
    (eventSource: string, token?: string, clickType?: IClickInfo.Type) =>
    () => {
      trackApparelDiscoveryShowroomV2Click(
        eventName,
        eventSource,
        requestId ?? ""
      );
      if (token && clickType) {
        trackClick(
          IClickInfo.build({
            token,
            type: clickType,
            container_name: eventName,
            request_id: requestId,
          })
        );
      }
    };

  const products = layoutElement.layout_element_data
    .map((layoutElementData) => layoutElementData.lean_product_tile)
    .filter(isNotUndefined);

  const searchString = useLocation().search;
  const filters = parseQueryParams(searchString).get("filters");
  const filterKeys = filters ? filters.split(",").filter(isNotUndefined) : [];
  const renderTile = (leanProductTile?: ILeanProductTile, index?: number) => {
    return (
      <SingleProductTile
        leanProductTile={leanProductTile}
        onClick={onClick}
        key={`shop-showroom-product-carousel-${index}`}
        category={category}
        filterKeys={filterKeys}
      />
    );
  };

  const onVisible = () => {
    trackApparelDiscoveryShowroomV2View(eventName, requestId ?? "", "");
  };

  const ref = useDoActionWhenVisible(onVisible);

  return (
    <div className={className} ref={ref}>
      <Wrapper>
        <TextWrapper>
          <Typography variant="displaySSerifRegular" color={Core.text.inverse}>
            {layoutElement.main_text}
          </Typography>
          <Spacer size="1x" />
          <Typography variant="paragraphSansRegular" color={Core.text.inverse}>
            {layoutElement.description_text}
          </Typography>
          <Spacer size="4x" />
          <StyledShopAllButton
            linkTo={layoutElement.cta_link}
            onClick={onClick(
              `shop-all-${layoutElement.category_path_identifier}`,
              layoutElement.category_path_identifier,
              IClickInfo.Type.CATEGORY
            )}
          >
            {layoutElement.cta_text}
          </StyledShopAllButton>
          <StyledAboutShowroomButton
            linkTo={categoryShowroom()}
            variant="tertiary"
            onClick={onClick(
              "about-showroom",
              SHOWROOM_PATH_IDENTIFIER,
              IClickInfo.Type.CATEGORY
            )}
          >
            {layoutElement.button_cta_text}
          </StyledAboutShowroomButton>
        </TextWrapper>
        <StyledFiniteScrollCarousel
          itemsPerSlide={ITEMS_PER_SLIDES}
          items={products}
          renderItem={renderTile}
          itemGap={ITEMS_GAP}
          peekGapPercentage={peekGapPercentage}
          hideWhenEndReached
          name="showroom-education-carousel"
        />
        <BlackOverlay />
      </Wrapper>
    </div>
  );
};

export const ShowroomEducationCarousel = observer(
  ShowroomEducationCarouselBase
);

const Wrapper = styled(Flex).attrs<IFlexProps, IFlexProps>({
  align: "center",
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})`
  background-color: ${Core.surface.enabled};
  width: 100%;
  padding: ${getSpacing("3x", 0, "4x")};
  position: relative;
  height: fit-content;
  margin-bottom: ${getSpacing("3x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("4x", 0, "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("5x", 0, "4x")};
  }
`;

const TextWrapper = styled(Column)`
  width: 200px;
  flex-shrink: 0;
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    padding: ${getSpacing(
      0,
      MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE,
      "3x",
      MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE
    )};
  }
  ${responsiveMediaQueries.tablet} {
    margin: ${getSpacing(0, "4x")};
  }
  ${responsiveMediaQueries.desktop} {
    margin: ${getSpacing(0, "5x")};
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    margin: ${getSpacing(0, "7x")};
  }
`;

const StyledShopAllButton = styled(Button).attrs<ButtonProps, ButtonProps>({
  variant: "secondary",
})`
  ${responsiveMediaQueries.mobile} {
    width: 168px;
  }
  &:active,
  &:focus,
  &:hover {
    background-color: ${Core.surface.inverse};
    color: ${Core.text.default};
  }
`;

const WholesalePrice = styled(Typography).attrs({
  color: Core.text.subdued,
  variant: "paragraphSansRegular",
})`
  transition: color 300ms ease-in-out;
`;

const StyledLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  ${responsiveMediaQueries.canHover} {
    &:hover {
      ${WholesalePrice} {
        color: ${Core.text.inverse};
      }
    }
  }
`;

const StyledFiniteScrollCarousel = styled(
  FiniteScrollCarousel.FiniteScrollCarousel as React.ComponentType<
    FiniteScrollCarousel.IProps<ILeanProductTile>
  >
)`
  min-width: 0;
  ${BaseFiniteScrollCarousel.Previous}, ${BaseFiniteScrollCarousel.Next} {
    color: white;
    z-index: 2;
    width: 40px;
    height: 40px;
    top: calc(50% - 27px);
  }
  ${BaseFiniteScrollCarousel.Previous} {
    left: -38px;
  }
  ${BaseFiniteScrollCarousel.Next} {
    right: 8px;
  }

  ${responsiveMediaQueries.mobile} {
    ${BaseFiniteScrollCarousel.Scrollable} {
      padding-left: ${MARKETPLACE_LEFT_AND_RIGHT_MARGIN_MOBILE}px;
    }
  }
`;

const BlackOverlay = styled.div`
  width: 238px;
  height: 100%;
  background: linear-gradient(
    270deg,
    ${Core.surface.enabled} 0%,
    rgba(0, 0, 0, 0) 88.01%
  );
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  pointer-events: none;
  ${responsiveMediaQueries.mobile} {
    display: none;
  }
`;

const StyledShowroomBadge = styled(ShowroomTileBadge)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
`;

const StyledAboutShowroomButton = styled(Button)`
  &&& {
    text-decoration-color: ${Core.text.inverse};
    width: fit-content;
    padding-top: ${getSpacing("2x")};
    color: ${Core.text.inverse};
  }
`;
